import React, { useState, useEffect, useCallback } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import daveyLogo from "assets/Davey_logotype_Yellow.svg";
import { useResponsive } from "hooks/useResponsive";
import { useEcoSaltEligibility } from "hooks/useEcoSaltUpgrade";
import { AUTH_SIGN_IN } from "utils/paths";
import { ThemeToggle } from "./shared/ThemeToggle";
import {
  LayoutContainer,
  SkipToContent,
  Header,
  HeaderLeft,
  Logo,
  AppTitle,
  UATBadge,
  UserSection,
  UserInfo,
  UserName,
  LogoutButton,
  ContentWrapper,
  Sidebar,
  SidebarNav,
  SidebarItem,
  HamburgerButton,
  CloseSidebarButton,
  ToggleButton,
  MainContent,
  Footer,
  MobileFooter,
  MobileLogoutButton,
  Overlay,
} from "styles/components/layout.styles";

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isProduction = process.env.REACT_APP_ENV === "production";
  const isAdmin = user?.role === "ADMIN";
  const { enabled: ecoSaltEnabled } = useEcoSaltEligibility();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  const closeMobileSidebar = useCallback(() => {
    setMobileOpen(false);
  }, []);

  useEffect(() => {
    if (!isMobile || !mobileOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") closeMobileSidebar();
    }

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isMobile, mobileOpen, closeMobileSidebar]);

  const handleLogout = () => {
    logout();
    navigate(AUTH_SIGN_IN);
  };

  return (
    <LayoutContainer>
      <SkipToContent href="#main-content">Skip to content</SkipToContent>
      <Header>
        {isMobile ? (
          <>
            <HeaderLeft>
              <Logo src={daveyLogo} alt="Davey Logo" />
              {!isProduction && <UATBadge>UAT</UATBadge>}
            </HeaderLeft>
            <HamburgerButton
              type="button"
              onClick={() => setMobileOpen((open) => !open)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              aria-controls="app-sidebar"
            >
              <i className={`pi ${mobileOpen ? "pi-times" : "pi-bars"}`} />
            </HamburgerButton>
          </>
        ) : (
          <>
            <HeaderLeft>
              <Logo src={daveyLogo} alt="Davey Logo" />
              <AppTitle>
                Pool Professional Dashboard
                {!isProduction && <UATBadge>UAT</UATBadge>}
              </AppTitle>
            </HeaderLeft>
            <UserSection>
              <UserInfo>
                Welcome,{" "}
                <UserName
                  type="button"
                  onClick={() => navigate("/profile")}
                  aria-label="Open my profile"
                >
                  {user?.firstName || "User"}
                </UserName>
              </UserInfo>
              <ThemeToggle />
              <LogoutButton type="button" onClick={handleLogout}>
                Logout
              </LogoutButton>
            </UserSection>
          </>
        )}
      </Header>

      <ContentWrapper>
        {isMobile && (
          <Overlay
            $visible={mobileOpen}
            onClick={closeMobileSidebar}
            aria-hidden={!mobileOpen}
          />
        )}

        <Sidebar
          id="app-sidebar"
          $collapsed={sidebarCollapsed}
          $mobileOpen={mobileOpen}
          aria-hidden={isMobile && !mobileOpen}
        >
          {isMobile && mobileOpen && (
            <CloseSidebarButton
              type="button"
              onClick={closeMobileSidebar}
              aria-label="Close menu"
            >
              <i className="pi pi-times" />
            </CloseSidebarButton>
          )}
          <SidebarNav>
            {isAdmin ? (
              <SidebarItem
                to="/all-devices"
                $active={location.pathname.startsWith("/all-devices")}
                $collapsed={!isMobile && sidebarCollapsed}
              >
                <i className="pi pi-th-large item-icon" />
                <span className="item-text">All devices</span>
              </SidebarItem>
            ) : (
              <SidebarItem
                to="/devices"
                $active={location.pathname.startsWith("/devices")}
                $collapsed={!isMobile && sidebarCollapsed}
              >
                <i className="pi pi-tablet item-icon" />
                <span className="item-text">My Devices</span>
              </SidebarItem>
            )}

            {isAdmin && (
              <SidebarItem
                to="/dealers"
                $active={location.pathname.startsWith("/dealers")}
                $collapsed={!isMobile && sidebarCollapsed}
              >
                <i className="pi pi-users item-icon" />
                <span className="item-text">Dealers</span>
              </SidebarItem>
            )}

            {isAdmin && (
              <SidebarItem
                to="/users"
                $active={location.pathname.startsWith("/users")}
                $collapsed={!isMobile && sidebarCollapsed}
              >
                <i className="pi pi-user item-icon" />
                <span className="item-text">Users</span>
              </SidebarItem>
            )}

            {ecoSaltEnabled && (
              <SidebarItem
                to="/ecosalt-upgrade"
                $active={location.pathname.startsWith("/ecosalt-upgrade")}
                $collapsed={!isMobile && sidebarCollapsed}
              >
                <i className="pi pi-bolt item-icon" />
                <span className="item-text">EcoSalt Upgrade</span>
              </SidebarItem>
            )}
            <SidebarItem
              to="/profile"
              $active={location.pathname.startsWith("/profile")}
              $collapsed={!isMobile && sidebarCollapsed}
            >
              <i className="pi pi-id-card item-icon" />
              <span className="item-text">Profile</span>
            </SidebarItem>
          </SidebarNav>

          {isMobile && (
            <MobileFooter>
              <ThemeToggle variant="mobile" />
              <MobileLogoutButton type="button" onClick={handleLogout}>
                <i className="pi pi-sign-out logout-icon" />
                <span>Logout</span>
              </MobileLogoutButton>
            </MobileFooter>
          )}

          {!isMobile && (
            <ToggleButton
              type="button"
              onClick={() => setSidebarCollapsed((c) => !c)}
              $isCollapsed={sidebarCollapsed}
              aria-label={
                sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
              }
            >
              <i
                className={`pi ${
                  sidebarCollapsed ? "pi-angle-right" : "pi-angle-left"
                }`}
              />
            </ToggleButton>
          )}
        </Sidebar>

        <MainContent id="main-content" tabIndex={-1}>
          <Outlet />
        </MainContent>
      </ContentWrapper>

      <Footer>
        <p>© 2025 Davey Pool Professional</p>
      </Footer>
    </LayoutContainer>
  );
};

export default Layout;
